/**
 * OrderChipRow — the new "sleek" UI surface for Imaging / Referrals /
 * Labs / Meds order entry, shipped Feb 28, 2026.
 *
 * Replaces the older bulky-dropdown rows with squared chip tags + a
 * tap-anchored popover for add/edit. The chips are 3-px-radius squared
 * tags (per user preference — no rounded pill / boxy cards). Each
 * section provides its own popover content via the `renderEditor`
 * render-prop so we keep the visual shell uniform while letting
 * specialty-specific fields (Type+BodyPart for imaging, Specialty for
 * referrals, lab checkboxes, med name+dose+freq) live next to their
 * domain logic.
 *
 * Mobile: popover anchors as a bottom sheet on screens ≤ 640 px so
 * the thumb can reach Save. Radix Popover handles outside-click and
 * Esc dismiss for free.
 *
 * Accent colors are taken from the existing chip palette so the four
 * sections retain their familiar color language:
 *   imaging  → primary (blue)
 *   referral → violet
 *   labs     → teal
 *   meds     → rose
 */
import React, { useState, useEffect } from 'react';
import { Plus, X, Star } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

const ACCENT_STYLES = {
  primary: {
    chipBg: 'rgba(108,182,255,0.14)',
    chipBorder: 'rgba(108,182,255,0.55)',
    chipText: '#6cb6ff',
  },
  violet: {
    chipBg: 'rgba(180,139,239,0.14)',
    chipBorder: 'rgba(180,139,239,0.55)',
    chipText: '#b48bef',
  },
  teal: {
    chipBg: 'rgba(86,209,204,0.14)',
    chipBorder: 'rgba(86,209,204,0.55)',
    chipText: '#56d1cc',
  },
  rose: {
    chipBg: 'rgba(255,123,134,0.14)',
    chipBorder: 'rgba(255,123,134,0.55)',
    chipText: '#ff7b86',
  },
  amber: {
    chipBg: 'rgba(240,183,90,0.14)',
    chipBorder: 'rgba(240,183,90,0.55)',
    chipText: '#f0b75a',
  },
};

/**
 * Filled order chip. Tap the body to re-open the editor; × removes;
 * ★ favorites (if onFavorite provided).
 */
const OrderChip = ({
  label,
  detail,
  starred,
  accent = 'primary',
  onClick,
  onRemove,
  onFavorite,
  testId,
}) => {
  const style = ACCENT_STYLES[accent] || ACCENT_STYLES.primary;
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1.5 select-none cursor-pointer transition-transform hover:-translate-y-px"
      style={{
        background: style.chipBg,
        border: `1px solid ${style.chipBorder}`,
        color: style.chipText,
        borderRadius: '3px',
        padding: '5px 6px 5px 9px',
        height: '26px',
        fontSize: '11.5px',
        fontWeight: 600,
        lineHeight: 1,
      }}
    >
      <span onClick={onClick} className="flex items-center gap-1.5">
        <span>{label}</span>
        {detail ? (
          <span style={{ color: 'var(--muted-foreground)', fontWeight: 500 }}>• {detail}</span>
        ) : null}
      </span>
      {onFavorite ? (
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onFavorite(); }}
          className="inline-flex items-center justify-center"
          title={starred ? 'Remove from Quick Actions' : 'Pin to Quick Actions'}
          style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
          data-testid={testId ? `${testId}-star` : undefined}
        >
          <Star
            className="w-3 h-3"
            style={{
              color: starred ? '#f0b75a' : 'rgba(255,255,255,0.35)',
              fill: starred ? '#f0b75a' : 'transparent',
            }}
          />
        </button>
      ) : null}
      <button
        type="button"
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onRemove && onRemove(); }}
        className="inline-flex items-center justify-center"
        title="Remove"
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: 'rgba(255,255,255,0.65)',
          fontSize: '16px',
          lineHeight: 1,
          padding: '0 4px',
          minWidth: '18px',
          minHeight: '18px',
        }}
        data-testid={testId ? `${testId}-remove` : undefined}
      >
        ×
      </button>
    </span>
  );
};

/**
 * The dashed "＋ Add …" chip that opens the editor popover. Acts as both
 * the trigger and the visual placeholder when the row is empty.
 */
const AddChip = React.forwardRef(({ label, accent = 'primary', testId, ...rest }, ref) => {
  const style = ACCENT_STYLES[accent] || ACCENT_STYLES.primary;
  return (
    <button
      ref={ref}
      type="button"
      data-testid={testId}
      className="inline-flex items-center gap-1.5 transition-transform hover:-translate-y-px hover:bg-primary/5 select-none"
      style={{
        background: 'transparent',
        border: `1px dashed ${style.chipBorder}`,
        color: style.chipText,
        borderRadius: '3px',
        padding: '5px 11px',
        height: '26px',
        fontSize: '11.5px',
        fontWeight: 600,
        lineHeight: 1,
        cursor: 'pointer',
      }}
      {...rest}
    >
      <Plus className="w-3.5 h-3.5" />
      <span>{label}</span>
    </button>
  );
});
AddChip.displayName = 'AddChip';

/**
 * OrderChipRow — the public component.
 *
 * @prop {Array}    items           — current list of structured entries
 * @prop {Function} renderChip      — (item, index) => { label, detail, starred }
 * @prop {Function} renderEditor    — ({ value, onSave, onCancel }) => ReactNode
 * @prop {string}   addLabel        — e.g. "Add imaging"
 * @prop {string}   accent          — primary | violet | teal | rose | amber
 * @prop {string}   sectionTitle    — e.g. "Imaging Orders"
 * @prop {ReactNode} sectionIcon    — small Lucide icon
 * @prop {string}   testIdPrefix    — for data-testid scaffolding
 * @prop {Function} onAdd           — called with the new entry from the editor
 * @prop {Function} onUpdate        — (index, entry) => void
 * @prop {Function} onRemove        — (index) => void
 * @prop {Function} onFavorite      — (entry) => void  (optional)
 * @prop {Function} isStarred       — (entry) => bool  (optional)
 * @prop {Function} getChipReason   — (item, index) => string | null  (optional)
 *                                    When present and returns a string, an
 *                                    italic "Auto-added · matched X" caption
 *                                    renders beneath the chip with a × that
 *                                    calls onClearChipReason(item, index).
 * @prop {Function} onClearChipReason — (item, index) => void  (optional)
 */
export const OrderChipRow = ({
  items = [],
  renderChip,
  renderEditor,
  addLabel = 'Add',
  accent = 'primary',
  sectionTitle,
  sectionIcon,
  testIdPrefix = 'chip',
  onAdd,
  onUpdate,
  onRemove,
  onFavorite,
  isStarred,
  getChipReason,
  onClearChipReason,
}) => {
  // Local popover state. `editIndex === -1` means "create new"; `null`
  // means closed. Keeping a single open-state means at most one popover
  // is on screen at a time which is what we want.
  const [openIndex, setOpenIndex] = useState(null);

  // Close popover on Escape (Radix already handles this but we do it
  // explicitly when the popover is anchored by an item chip — Radix's
  // dismissable layer can sometimes lose focus when the chip rerenders).
  useEffect(() => {
    if (openIndex === null) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpenIndex(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [openIndex]);

  const handleSave = (entry) => {
    if (openIndex === -1) {
      onAdd && onAdd(entry);
    } else if (typeof openIndex === 'number') {
      onUpdate && onUpdate(openIndex, entry);
    }
    setOpenIndex(null);
  };

  return (
    <div data-testid={`${testIdPrefix}-row`}>
      {sectionTitle && (
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
            {sectionIcon}
            {sectionTitle}
          </span>
          {items.length > 0 && (
            <span
              className="text-[9px] font-bold text-foreground bg-secondary/60 border border-border px-1.5 py-0.5"
              style={{ borderRadius: '2px' }}
              data-testid={`${testIdPrefix}-count`}
            >
              {items.length}
            </span>
          )}
        </div>
      )}
      <div className="flex flex-wrap gap-x-1.5 gap-y-2 items-start">
        {items.map((item, index) => {
          const meta = renderChip ? renderChip(item, index) : { label: '?', detail: '' };
          const why = typeof getChipReason === 'function' ? getChipReason(item, index) : null;
          return (
            <div key={`chip-${index}`} className="flex flex-col items-start gap-0.5 min-w-0">
              <Popover
                open={openIndex === index}
                onOpenChange={(o) => setOpenIndex(o ? index : null)}
              >
                <PopoverTrigger asChild>
                  <span>
                    <OrderChip
                      label={meta.label}
                      detail={meta.detail}
                      accent={accent}
                      starred={typeof isStarred === 'function' ? isStarred(item) : !!meta.starred}
                      onClick={() => setOpenIndex(index)}
                      onRemove={() => onRemove && onRemove(index)}
                      onFavorite={onFavorite ? () => onFavorite(item) : undefined}
                      testId={`${testIdPrefix}-${index}`}
                    />
                  </span>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  sideOffset={6}
                  className="p-3"
                  style={{ width: 320, borderRadius: 4 }}
                  data-testid={`${testIdPrefix}-${index}-popover`}
                >
                  {renderEditor && renderEditor({
                    value: item,
                    onSave: handleSave,
                    onCancel: () => setOpenIndex(null),
                    mode: 'edit',
                  })}
                </PopoverContent>
              </Popover>
              {/* Auto-seed "Why?" mini-explainer (Feb 28, 2026 v6.8.43).
                  Renders as a tiny italic caption directly under the
                  chip with a × dismiss that clears just the caption
                  (the chip itself stays — × on the chip removes the
                  chip). Helps the provider eyeball why a chip
                  suddenly appeared without breaking the layout. */}
              {why && (
                <div
                  className="flex items-center gap-1 max-w-[220px]"
                  data-testid={`${testIdPrefix}-${index}-why`}
                >
                  <span className="text-[9px] italic text-muted-foreground/80 leading-tight truncate">
                    auto · {why}
                  </span>
                  {onClearChipReason && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onClearChipReason(item, index); }}
                      className="text-muted-foreground/50 hover:text-muted-foreground transition-colors shrink-0"
                      title="Hide this auto-seed note (keeps the chip)"
                      data-testid={`${testIdPrefix}-${index}-why-dismiss`}
                      style={{ fontSize: '9px', lineHeight: 1 }}
                    >
                      ×
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
        <Popover
          open={openIndex === -1}
          onOpenChange={(o) => setOpenIndex(o ? -1 : null)}
        >
          <PopoverTrigger asChild>
            <AddChip
              label={addLabel}
              accent={accent}
              testId={`${testIdPrefix}-add`}
              onClick={() => setOpenIndex(-1)}
            />
          </PopoverTrigger>
          <PopoverContent
            align="start"
            sideOffset={6}
            className="p-3"
            style={{ width: 320, borderRadius: 4 }}
            data-testid={`${testIdPrefix}-add-popover`}
          >
            {renderEditor && renderEditor({
              value: null,
              onSave: handleSave,
              onCancel: () => setOpenIndex(null),
              mode: 'create',
            })}
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

export default OrderChipRow;
